import { Injectable } from '@angular/core';
import { BaseViewerLayer } from '@App/app/engine/viewer-layers/base-viewer-layer';
import { LAYER_TYPES, LAYER_WITH_THICKNESS } from '@App/app/entities/layer/enums/layer-types.enum';
import { LayerUI, LayerUiExpandable } from '@App/app/entities/layer/layer-ui.model';
import { TubeData } from '@App/app/entities/layer/tube.model';
import { UnitsSystem } from '@App/app/entities/shared/units.model';
import { ViewerLayerCameraService } from './viewer-layer-camera.service';
import { ViewerLayerRegistryService } from './viewer-layer-registry.service';

@Injectable({
  providedIn: 'root',
})
export class ViewerLayerService {
  layersLoaded$ = this.registry.layersLoaded$;

  constructor(
    private registry: ViewerLayerRegistryService,
    private cameraService: ViewerLayerCameraService,
  ) {}

  private layer(id: number | string): BaseViewerLayer {
    return this.registry.getLayer(id) as BaseViewerLayer;
  }

  initViewerLayers(layers: (LayerUI | LayerUiExpandable)[]): void {
    this.registry.initViewerLayers(layers);
  }

  destroyBasePlane(): void {
    this.registry.getBasePlane()?.destroyLayer();
  }

  getThicknessFormControls(layer: LayerUI, unitsSystem: UnitsSystem) {
    return this.layer(layer.id).getThicknessFormControls(unitsSystem);
  }

  updateThickness(
    layer: LayerUI,
    value: number,
    fieldName?: keyof Omit<TubeData, 'edges' | 'length'>,
  ): void {
    this.layer(layer.id).updateThickness(
      value,
      fieldName as keyof Omit<TubeData, 'edges' | 'length'>,
    );
  }

  isLayerSaved(id: number) {
    return this.registry.getLayer(id)?.layerViewer?.isSaved;
  }

  deleteLayer(id: number): void {
    this.registry.deleteLayer(id);
  }

  hasGuiToggler(id: number) {
    return this.registry.getLayer(id)?.hasGuiToggler;
  }

  hasThickness(id: number): boolean {
    const { type } = this.layer(id).layerViewer;
    const values = (Object.values(LAYER_WITH_THICKNESS) as unknown) as LAYER_TYPES[];
    return values.includes(type as NonNullable<typeof type>);
  }

  hasActiveLayerViewer(id: number): boolean {
    return !!this.layer(id).hasActiveLayerViewer();
  }

  createBackupLayer(layer: LayerUI | LayerUiExpandable): void {
    this.layer(layer.id).createBackupLayer(layer);
  }

  loadBackupLayer(id: number): void {
    this.layer(id).loadBackupLayer();
  }

  setLayerEditMode(id: number, value: boolean): void {
    this.layer(id).setLayerEditMode(value, id);
  }

  showLayer(layer: LayerUI | LayerUiExpandable): void {
    this.layer(layer.id).showLayer(layer);
  }

  showLayers(layers: (LayerUI | LayerUiExpandable)[]): void {
    for (const layer of layers) {
      this.layer(layer.id).showLayer(layer);
    }
  }

  hideLayer(id: number): void {
    this.layer(id).hideLayer();
  }

  hideLayers(layers: (LayerUI | LayerUiExpandable)[]): void {
    for (const layer of layers) {
      this.layer(layer.id).hideLayer();
    }
  }

  showLayerGui(id: number): void {
    const viewerLayer = this.layer(id);
    if (viewerLayer.hasGuiToggler) {
      viewerLayer.showLayerGui();
    }
  }

  hideLayerGui(id: number): void {
    this.layer(id).hideLayerGui();
  }

  changeLayerViewerName(id: number, text: string): void {
    const viewerLayer = this.layer(id);
    viewerLayer.changeLayerViewerName(text);
    if (this.checkIsGuiVisible(id)) {
      viewerLayer.changeLayerNameOnGui(text);
    }
  }

  getData(id: number) {
    return this.layer(id).getData();
  }

  getBackupData(id: number) {
    return this.layer(id).getBackupData();
  }

  checkIsGuiVisible(id: number) {
    return this.registry.getLayer(id)?.checkGuiIsVisible();
  }

  addTmpLayer(layer: LayerUI | LayerUiExpandable): void {
    this.initViewerLayer(layer);
    this.showLayer(layer);
    this.createBackupLayer(layer);
    this.setLayerEditMode(layer.id, true);
  }

  toggleImperialGui(isImperial: boolean): void {
    this.registry.forEachLayer((viewerLayer) => viewerLayer.toggleImperialGui(isImperial));
  }

  checkViewerLayerExists(id: number): boolean {
    return this.registry.exists(id);
  }

  getTmpLayerData(id: number) {
    return this.getData(id);
  }

  setNewParentId(id: number, parentId: number): void {
    this.layer(id).setParentId(parentId);
  }

  deleteAllLocal(): void {
    this.registry.deleteAllLocal();
  }

  setCameraOnLayer(layer: LayerUI): void {
    const targetData = this.layer(layer.id).getCameraTargetData();
    if (targetData) {
      this.cameraService.setCameraOnLayerTarget(targetData, layer.type);
    }
  }

  getBasePlane() {
    return this.registry.getBasePlane();
  }

  initViewerLayer(layer: LayerUI | LayerUiExpandable, setAsLayerViewer = false): void {
    this.registry.initViewerLayer(layer, setAsLayerViewer);
  }
}
